import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteFreeSoloValueMapping, AutocompleteProps, AutocompleteRenderOptionState, TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";
import Api from "../../../../../core/services/api/api";
import {DefaultPaginationInfo} from "../../../../../core/constants/enums";


interface RegionOption {
    id: number;
    title?: string;
}

interface RegionAutocompleteProps<T = RegionOption, M extends boolean = false, DC extends boolean = false, FS extends boolean = false> extends Omit<AutocompleteProps<T, M, DC, FS>, 'renderInput' | 'renderOption' | 'options'> {
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;
    transformRecord?: (record: RegionOption) => T;
    renderOption?: (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: T,
        state: AutocompleteRenderOptionState,
        defaultRender: (props: React.HTMLAttributes<HTMLLIElement>, option: T) => React.ReactNode
    ) => React.ReactNode;

    [key: string]: any;
}

const RegionAutocomplete = <T = RegionOption, M extends boolean = false, DC extends boolean = false, FS extends boolean = false>({
                                                                                                                                 textFieldProps = {},
                                                                                                                                 form = false,
                                                                                                                                 name = undefined,
                                                                                                                                 required = false,
                                                                                                                                 setLoadingOptions: setLoadingOptionsProps,
                                                                                                                                 renderOption: customRenderOption,
                                                                                                                                 transformRecord,
                                                                                                                                 ...props
                                                                                                                             }: RegionAutocompleteProps<T, M, DC, FS>) => {
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    /**
     * Syncs the internal loading state with the parent component.
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * Fetches system region options based on search keywords.
     *
     * @param cancelToken - Axios cancel token for request cancellation
     * @param keywords - Search query string
     * @returns Array of matching region options
     */
    const getOptions = useCallback(async (cancelToken: CancelToken, keywords?: string) => {
        setLoadingOptions(true);
        try {
            const response = await Api.getSystemRegions({
                pagination: {
                    ...DefaultPaginationInfo,
                    pageSize: 20,
                },
                title: keywords,
            }, cancelToken);
            if (!isMounted())
                return [];
            return response?.data?.map(e => transformRecord?.(e) ?? e as RegionOption) ?? [];
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [isMounted, transformRecord]);

    /**
     * Determines if two region options are equal based on their IDs.
     */
    const isOptionEqualToValue = useCallback((option: RegionOption, value: RegionOption | null) =>
        option.id === value?.id, []);

    /**
     * Extracts the display label from a region option.
     */
    const getOptionLabel = useCallback((option: RegionOption | AutocompleteFreeSoloValueMapping<FS>) =>
        props.freeSolo === true ? option : (option as RegionOption)?.title ?? '', [props.freeSolo]);

    /**
     * Gets the unique key for a region option.
     */
    const getOptionKey = useCallback((option: RegionOption | AutocompleteFreeSoloValueMapping<FS>) =>
        props.freeSolo === true ? option : String((option as RegionOption)?.id ?? ''), [props.freeSolo]);

    const defaultOptions = useMemo(() => [], []);

    return (
        <AutocompleteBase<T, M, DC, FS>
            keywordsBased
            canSearchEmptyKeywords
            getOptions={getOptions}
            timeout={1000}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue as (option: T, value: T | null) => boolean}
            getOptionLabel={getOptionLabel as (option: T | AutocompleteFreeSoloValueMapping<FS>) => string}
            getOptionKey={getOptionKey as (option: T | AutocompleteFreeSoloValueMapping<FS>) => string}
            options={defaultOptions}
            clearOnBlur={false}
            {...props}
        />
    );
};

export default RegionAutocomplete;
